import { FC, ReactElement, useEffect, useState } from 'react';

import { skipToken } from '@reduxjs/toolkit/query';

import Paginator from 'components/paginator/Paginator';

import { BildResursData, GranskningResursIndex, RitningResursData } from 'models';

import { DokumentationstypEnum, GenreFormStriktEnum } from 'enums';

import { ArkivResurs } from './Arkivresurs';
import { MellanlagerResurs } from './Mellanlagerresurs';

import { usePostArkivAdvancedSearchMutation } from 'api/endpoints/arkivresursSlice';
import { useGetDokumentationQuery } from 'api/endpoints/dokumentationSlice';
import classNames from 'classnames';
import { toMellanlagerResurs, toResursDataFromSearchIndexResult } from 'utilities/mappers';

interface BebyggelseDokumentationProps {
  itemsPerPage?: number;
  uri?: string;
  bebyggelseobjektId?: string;
  dokumentationstyp: DokumentationstypEnum;
  onLoadingStateChange: (isLoading: boolean) => void;
  dokumentation?: Array<GranskningResursIndex>;
}

const DokumentationPresentation: FC<BebyggelseDokumentationProps> = ({
  uri,
  itemsPerPage = 8,
  bebyggelseobjektId,
  dokumentationstyp,
  onLoadingStateChange
}): ReactElement => {
  const [mellanlagerresursCurrentPage, setMellanlagerresursCurrentPage] = useState(1);
  const [arkivresursCurrentPage, setArkivresursCurrentPage] = useState(1);

  const [arkivResursdata, setArkivResursdata] = useState<Array<BildResursData | RitningResursData>>([]);
  const [mellanlagerresursdata, setMellanlagerresursdata] = useState<Array<BildResursData | RitningResursData>>([]);

  const [filteredDokumentation, setFilteredDokumentation] = useState<GranskningResursIndex[]>([]);

  const [postArkivSearch, { data: postArkivSearchResults, isLoading: postArkivSearchIsLoading }] =
    usePostArkivAdvancedSearchMutation();

  const {
    data: dokumentation,
    isSuccess: fetchDokumentationIsSuccess,
    isFetching: fetchDokumentationIsFetching
  } = useGetDokumentationQuery(bebyggelseobjektId ?? skipToken);

  useEffect(() => {
    if (fetchDokumentationIsSuccess) {
      setFilteredDokumentation(dokumentation.filter((d) => d.dokumentationstyp === dokumentationstyp));
    }
  }, [dokumentation, dokumentationstyp, fetchDokumentationIsSuccess]);

  useEffect(() => {
    const isLoading = postArkivSearchIsLoading || fetchDokumentationIsFetching;
    onLoadingStateChange(isLoading);
  }, [postArkivSearchIsLoading, fetchDokumentationIsFetching, onLoadingStateChange]);

  useEffect(() => {
    const start = (mellanlagerresursCurrentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    const paginatedDokumentation = filteredDokumentation.slice(start, end);

    setMellanlagerresursdata(paginatedDokumentation.map((d) => toMellanlagerResurs(d)));
  }, [mellanlagerresursCurrentPage, itemsPerPage, filteredDokumentation]);

  useEffect(() => {
    if (uri) {
      const pubDokumentUrl = `${new URL(uri).origin}/dokumentation/search/advanced`;

      postArkivSearch({
        url: pubDokumentUrl,
        genre_form_strikt:
          dokumentationstyp === DokumentationstypEnum.BILD
            ? GenreFormStriktEnum.FOTOGRAFI
            : GenreFormStriktEnum.RITNING,
        kontext_uri: uri,
        page: arkivresursCurrentPage - 1,
        pagesize: itemsPerPage
      });
    }
  }, [arkivresursCurrentPage, dokumentationstyp, itemsPerPage, postArkivSearch, uri]);

  useEffect(() => {
    if (postArkivSearchResults && postArkivSearchResults?.totalElements > 0) {
      const resursdata = postArkivSearchResults.indexResults
        .filter((res) =>
          res.genreFormStrikt === DokumentationstypEnum.BILD
            ? GenreFormStriktEnum.FOTOGRAFI
            : GenreFormStriktEnum.RITNING
        )
        .map((res) => toResursDataFromSearchIndexResult(res));

      setArkivResursdata(resursdata);
    }
  }, [itemsPerPage, postArkivSearchResults]);

  return (
    <>
      {!filteredDokumentation.length && postArkivSearchResults?.totalElements === 0 ? (
        <p>Inga {dokumentationstyp === DokumentationstypEnum.BILD ? 'fotografier' : 'situationsplaner'}</p>
      ) : (
        <>
          {filteredDokumentation.length > 0 && (
            <ImagesWithPaginator
              heading={`${
                dokumentationstyp === DokumentationstypEnum.BILD ? 'Fotografier' : 'Situationsplaner'
              } (utkast)`}
              liElementKey={`${dokumentationstyp}-utkast`}
              resursdata={mellanlagerresursdata}
              currentPage={mellanlagerresursCurrentPage}
              itemsPerPage={itemsPerPage}
              totalElements={filteredDokumentation.length}
              setCurrentPage={setMellanlagerresursCurrentPage}
            />
          )}

          {arkivResursdata.length > 0 && (
            <ImagesWithPaginator
              heading={`${dokumentationstyp === DokumentationstypEnum.BILD ? 'Fotografier' : 'Situationsplaner'}`}
              liElementKey={dokumentationstyp}
              resursdata={arkivResursdata}
              currentPage={arkivresursCurrentPage}
              itemsPerPage={itemsPerPage}
              totalElements={(postArkivSearchResults && postArkivSearchResults.totalElements) || 0}
              setCurrentPage={setArkivresursCurrentPage}
            />
          )}
        </>
      )}
    </>
  );
};

type ImagesWithPaginatorProps = {
  heading: string;
  liElementKey: string;
  resursdata: Array<BildResursData | RitningResursData>;
  currentPage: number;
  itemsPerPage: number;
  totalElements: number;
  setCurrentPage: (page: number) => void;
};

const ImagesWithPaginator = ({
  heading,
  liElementKey,
  resursdata,
  currentPage,
  itemsPerPage,
  totalElements,
  setCurrentPage
}: ImagesWithPaginatorProps) => {
  return (
    <div
      className={classNames('tw-mb-6 tw-border-b tw-border-b-raa-gray-3 last:tw-border-0', {
        'tw-pb-8': Math.ceil(totalElements / itemsPerPage) === 1
      })}
    >
      <h3 className="tw-heading-sm">{heading}</h3>
      <div>
        <ul className="tw-grid tw-grid-cols-2 tw-gap-4 md:tw-grid-cols-3 lg:tw-grid-cols-4">
          {resursdata.map((resurs, index) => (
            <li key={`${liElementKey}-${index}`} className="tw-w-full">
              {resurs.uri ? (
                <ArkivResurs resursdata={resurs} shouldLoad={true} />
              ) : (
                <MellanlagerResurs resurs={resurs} />
              )}
            </li>
          ))}
        </ul>
      </div>

      {Math.ceil(totalElements / itemsPerPage) > 1 && (
        <Paginator
          currentPage={currentPage}
          totalPages={Math.ceil(totalElements / itemsPerPage)}
          onPageChange={(page) => setCurrentPage(page)}
        />
      )}
    </div>
  );
};

export default DokumentationPresentation;
